import { useState } from "react";
import {
    ArrowTopRightOnSquareIcon,
    ChevronDownIcon,
    DocumentIcon,
    DocumentTextIcon,
    ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import type { DocumentCategory } from "../../../../generated/enums.ts";
import { DOCUMENT_DIRECTION_LABELS } from "../../../../types/enums.ts";
import { DisplayLocale } from "../../../../lib/Money.ts";
import {
    DEFAULT_CURRENCY,
    QuinnCopy,
    type QuinnDocumentSource,
    type QuinnSourceBuckets,
    type QuinnTransactionSource,
} from "../../types";
import {
    documentSourceCounterparty,
    documentSourceDate,
    documentSourceHref,
    formatQuinnAmount,
} from "../../utils";

function formatSourceDate(value?: string | null): string {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString(DisplayLocale.NL_NL, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function documentIcon(category: DocumentCategory | null | undefined) {
    if (category === "invoice") return DocumentTextIcon;
    if (category === "pos_receipt") return ReceiptPercentIcon;
    return DocumentIcon;
}

function TransactionSourceRow({ transaction }: { transaction: QuinnTransactionSource }) {
    const isInflow = transaction.flow === "inflow";
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-2.5">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-900">
                        {transaction.description || QuinnCopy.NoDescription}
                    </p>
                    {transaction.relation_name && (
                        <p className="mt-0.5 truncate text-[11px] text-gray-500">{transaction.relation_name}</p>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
                        {formatSourceDate(transaction.booking_date) && (
                            <span>{formatSourceDate(transaction.booking_date)}</span>
                        )}
                        {transaction.generalLedgerAccountLabel && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                                {transaction.generalLedgerAccountLabel}
                            </span>
                        )}
                    </div>
                </div>
                <span
                    className={`shrink-0 text-xs font-semibold tabular-nums ${
                        isInflow ? "text-emerald-600" : "text-rose-600"
                    }`}
                >
                    {isInflow ? "+" : "−"}
                    {formatQuinnAmount(transaction.amount ?? 0, transaction.currency ?? DEFAULT_CURRENCY)}
                </span>
            </div>
        </div>
    );
}

function DocumentSourceRow({ document }: { document: QuinnDocumentSource }) {
    const Icon = documentIcon(document.document_category);
    const payable = document.scanned_fields?.total_amount_payable;
    const signedAmount = payable?.signed_amount;
    const currency = payable?.currency ?? DEFAULT_CURRENCY;
    const counterparty = documentSourceCounterparty(document);
    const date = formatSourceDate(documentSourceDate(document));
    const href = documentSourceHref(document);

    const content = (
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <p className="truncate text-xs font-medium text-gray-900">
                        {document.file_name || QuinnCopy.UnknownDocument}
                    </p>
                </div>
                {counterparty && (
                    <p className="mt-0.5 truncate text-[11px] text-gray-500">{counterparty}</p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
                    {date && <span>{date}</span>}
                    {document.document_direction && (
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">
                            {DOCUMENT_DIRECTION_LABELS[document.document_direction] ?? document.document_direction}
                        </span>
                    )}
                </div>
            </div>
            {signedAmount != null && (
                <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-900">
                    {formatQuinnAmount(signedAmount, currency)}
                </span>
            )}
            {href && <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />}
        </div>
    );

    if (!href) {
        return <div className="rounded-xl border border-gray-200 bg-white p-2.5">{content}</div>;
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={QuinnCopy.OpenDocumentInNewTab}
            aria-label={`${document.file_name || QuinnCopy.UnknownDocument}: ${QuinnCopy.OpenDocumentInNewTab}`}
            className="block rounded-xl border border-gray-200 bg-white p-2.5 transition-colors hover:border-primary/40 hover:bg-gray-50"
        >
            {content}
        </a>
    );
}

function ForecastSourceRow({ forecast }: { forecast: Record<string, unknown> }) {
    const date = formatSourceDate(typeof forecast.date === "string" ? forecast.date : null);
    const balance =
        typeof forecast.forecasted_balance === "number" ? forecast.forecasted_balance : null;
    const currency = typeof forecast.currency === "string" ? forecast.currency : DEFAULT_CURRENCY;
    const label =
        (typeof forecast.iban === "string" && forecast.iban) ||
        (typeof forecast.account_name === "string" && forecast.account_name) ||
        "Cashflow prognose";

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-2.5">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-900">{label}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
                        {date && <span>{date}</span>}
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">Prognose</span>
                    </div>
                </div>
                {balance != null && (
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-900">
                        {formatQuinnAmount(balance, currency)}
                    </span>
                )}
            </div>
        </div>
    );
}

export function QuinnSources({ sources }: { sources: QuinnSourceBuckets }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const total = sources.transactions.length + sources.documents.length + sources.forecasts.length;
    if (total === 0) return null;

    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={() => setIsExpanded((current) => !current)}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                aria-expanded={isExpanded}
            >
                {total} {total === 1 ? "bron" : "bronnen"}
                <ChevronDownIcon
                    className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                />
            </button>

            {isExpanded && (
                <div className="mt-2 space-y-1.5">
                    {sources.transactions.map((transaction, index) => (
                        <TransactionSourceRow key={`transaction-${index}`} transaction={transaction} />
                    ))}
                    {sources.documents.map((document, index) => (
                        <DocumentSourceRow key={`document-${index}`} document={document} />
                    ))}
                    {sources.forecasts.map((forecast, index) => (
                        <ForecastSourceRow key={`forecast-${index}`} forecast={forecast} />
                    ))}
                </div>
            )}
        </div>
    );
}
